import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, MenuItem,
  Stack, Step, StepLabel, Stepper, Switch, TextField, Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import VerifiedIcon from '@mui/icons-material/Verified';
import EditIcon from '@mui/icons-material/Edit';

import { PageHeader } from '@/components/PageHeader';
import { useApiMutation, useApiQuery } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { DDIPanel, type SafetySnapshot } from '@/components/clinical/DDIPanel';
import { clinician } from '@/api/endpoints';
import { api } from '@/api/client';
import type {
  ClinicianLicense, DrugCatalogEntry, Prescription, UUID,
} from '@/types/medical';

/**
 * Rx composer. Three steps:
 *
 *   1. Compose      — patient, license, items (drug + dose + qty + refills)
 *   2. Safety       — POST /check/, render DDIPanel; show block if regulator says no
 *   3. Sign         — POST /sign/ (server enforces no-pending-AI-event for any
 *                     attached AI suggestion; we don't block here)
 *
 * After step 3 we redirect to the prescription detail page.
 *
 * The composer is a "new prescription" UI; amendment routes here too with
 * an `?amends=<rx_id>` param so we can prefill items from the original.
 */

type DraftItem = {
  drug_id: number | null;
  drug_name: string;
  drug_atc: string;
  dose_text: string;
  quantity_text: string;
  refills: number;
  is_substitution_allowed: boolean;
  is_long_term: boolean;
  notes_to_pharmacist: string;
  notes_to_patient: string;
  indication_icd_code: string;
};

type Step = 0 | 1 | 2;

export function ClinicianPrescriptionComposerPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [params] = useSearchParams();

  const initialAppointment = params.get('appointment') ?? '';

  // ─── Step state ──────────────────────────────────────────────────────
  const [step, setStep] = useState<Step>(0);
  const [patientId, setPatientId] = useState<string>('');
  const [licenseId, setLicenseId] = useState<string>('');
  const [validUntil, setValidUntil] = useState<string>('');
  const [isEmergency, setIsEmergency] = useState(false);
  const [isRepeat, setIsRepeat] = useState(false);
  const [items, setItems] = useState<DraftItem[]>([emptyItem()]);

  // The created draft prescription (server-side row) and its safety check.
  const [draftRx, setDraftRx] = useState<Prescription | null>(null);
  const [safety, setSafety] = useState<SafetySnapshot | null>(null);

  // ─── Lookups ─────────────────────────────────────────────────────────
  const meQ = useApiQuery({
    queryKey: ['clinician', 'me'],
    queryFn:  clinician.me.get,
  });

  // We expose licenses through ClinicianProfile.trust_passport.active_jurisdiction_codes,
  // but for the composer we want the actual ClinicianLicense rows (PK + can_prescribe).
  // The backend serializer doesn't expose them inside `me`, so we fetch them directly.
  const licensesQ = useApiQuery({
    queryKey: ['clinician', 'licenses'],
    queryFn:  () => api.get<ClinicianLicense[]>('/api/medical/clinician/licenses/'),
  });

  // ─── Mutations ───────────────────────────────────────────────────────
  const createDraftMut = useApiMutation({
    mutationFn: (payload: object) => clinician.prescriptions.create(payload),
    onSuccess: (rx) => {
      setDraftRx(rx);
      toast.success('Draft created.');
      // Step is advanced by the explicit "Continue" button after this.
    },
    onError: (e) => toast.error(e.message),
  });

  const checkMut = useApiMutation({
    mutationFn: (id: UUID) => clinician.prescriptions.check(id),
    onSuccess: (result) => {
      // The check returns the snapshot inline.
      setSafety({
        overall_severity: (result.overall_severity as SafetySnapshot['overall_severity']) ?? 'none',
        findings: (result.findings as SafetySnapshot['findings']) ?? [],
      });
    },
    onError: (e) => {
      // Backend raises PrescribingBlocked → 422 with detail; surface and
      // record a synthetic snapshot so the user sees the wall.
      toast.error(e.message);
      setSafety({
        overall_severity: 'major',
        findings: [{
          finding: 'regulation_engine',
          severity: 'major',
          rule_type: 'unknown',
          reason: e.message,
          user_message: e.message,
        }],
        regulation_decision_effect: 'block',
        regulation_user_message: e.message,
      });
    },
  });

  const signMut = useApiMutation({
    mutationFn: (id: UUID) => clinician.prescriptions.sign(id),
    onSuccess: (rx) => {
      toast.success(rx.status === 'registered' ? 'Signed and registered.' : 'Signed.');
      qc.invalidateQueries({ queryKey: ['clinician', 'prescriptions'] });
      nav(`/clinician/prescriptions/${rx.id}`);
    },
    onError: (e) => toast.error(e.message),
  });

  // ─── Validation ─────────────────────────────────────────────────────
  const canCreateDraft = useMemo(() => (
    Boolean(patientId) &&
    Boolean(licenseId) &&
    items.length > 0 &&
    items.every((it) => it.drug_id != null && it.dose_text.trim() && it.quantity_text.trim())
  ), [patientId, licenseId, items]);

  const blockedBySafety =
    safety?.regulation_decision_effect === 'block' ||
    safety?.overall_severity === 'contra';

  // ─── Effects ────────────────────────────────────────────────────────
  // Auto-pick the first prescribing license once licenses load.
  useEffect(() => {
    if (!licenseId && licensesQ.data) {
      const first = licensesQ.data.find((l) => l.can_prescribe && l.status === 'active');
      if (first) setLicenseId(String(first.id));
    }
  }, [licensesQ.data, licenseId]);

  // ─── Step actions ───────────────────────────────────────────────────
  const submitDraft = () => {
    if (!canCreateDraft) return;
    const payload = {
      patient_id: parseInt(patientId, 10),
      appointment_id: initialAppointment || null,
      license_id: parseInt(licenseId, 10),
      valid_until: validUntil || null,
      is_repeat: isRepeat,
      is_emergency: isEmergency,
      items: items.map((it) => ({
        drug_id: it.drug_id,
        dose_text: it.dose_text,
        quantity_text: it.quantity_text,
        refills: it.refills,
        is_substitution_allowed: it.is_substitution_allowed,
        is_long_term: it.is_long_term,
        notes_to_pharmacist: it.notes_to_pharmacist,
        notes_to_patient: it.notes_to_patient,
        indication_icd_code: it.indication_icd_code,
      })),
    };
    createDraftMut.mutate(payload);
  };

  const runSafetyCheck = () => {
    if (!draftRx) return;
    setSafety(null);
    checkMut.mutate(draftRx.id);
  };

  const sign = () => {
    if (!draftRx || blockedBySafety) return;
    signMut.mutate(draftRx.id);
  };

  // ─── Render ─────────────────────────────────────────────────────────
  return (
    <>
      <PageHeader
        title={draftRx ? 'Prescription draft' : 'New prescription'}
        breadcrumbs={[
          { label: 'Prescriptions', to: '/clinician/prescriptions' },
          { label: draftRx ? draftRx.id.slice(0, 8) : 'New' },
        ]}
      />

      <Stepper activeStep={step} sx={{ mb: 4 }}>
        <Step><StepLabel>Compose</StepLabel></Step>
        <Step><StepLabel>Safety check</StepLabel></Step>
        <Step><StepLabel>Sign &amp; register</StepLabel></Step>
      </Stepper>

      {step === 0 && (
        <ComposeStep
          meName={meQ.data?.display_name ?? ''}
          licenses={licensesQ.data ?? []}
          patientId={patientId}        onPatientId={setPatientId}
          licenseId={licenseId}        onLicenseId={setLicenseId}
          validUntil={validUntil}      onValidUntil={setValidUntil}
          isEmergency={isEmergency}    onIsEmergency={setIsEmergency}
          isRepeat={isRepeat}          onIsRepeat={setIsRepeat}
          items={items}                onItemsChange={setItems}
          locked={Boolean(draftRx)}
        />
      )}

      {step === 1 && safety && (
        <SafetyStep
          snapshot={safety}
          onRecheck={runSafetyCheck}
          rechecking={checkMut.isPending}
        />
      )}

      {step === 2 && draftRx && (
        <SignStep rx={draftRx} blocked={blockedBySafety} />
      )}

      {/* Step nav */}
      <Stack direction="row" spacing={1} sx={{ mt: 3 }} justifyContent="flex-end">
        {step > 0 && (
          <Button onClick={() => setStep((step - 1) as Step)}
                  disabled={createDraftMut.isPending || checkMut.isPending || signMut.isPending}>
            Back
          </Button>
        )}

        {step === 0 && !draftRx && (
          <Button
            variant="contained"
            disabled={!canCreateDraft || createDraftMut.isPending}
            onClick={submitDraft}
          >
            Create draft
          </Button>
        )}
        {step === 0 && draftRx && (
          <Button
            variant="contained"
            onClick={() => { runSafetyCheck(); setStep(1); }}
            startIcon={<HealthAndSafetyIcon />}
          >
            Run safety check
          </Button>
        )}
        {step === 1 && safety && (
          <Button
            variant="contained"
            disabled={blockedBySafety || checkMut.isPending}
            onClick={() => setStep(2)}
            color={blockedBySafety ? 'inherit' : 'primary'}
          >
            {blockedBySafety ? 'Blocked — cannot continue' : 'Continue to sign'}
          </Button>
        )}
        {step === 2 && (
          <Button
            variant="contained" color="success"
            startIcon={<VerifiedIcon />}
            disabled={blockedBySafety || signMut.isPending}
            onClick={sign}
          >
            Sign &amp; register
          </Button>
        )}
      </Stack>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 0 — Compose
// ─────────────────────────────────────────────────────────────────────────────

function ComposeStep({
  meName, licenses, patientId, onPatientId, licenseId, onLicenseId,
  validUntil, onValidUntil, isEmergency, onIsEmergency,
  isRepeat, onIsRepeat, items, onItemsChange, locked,
}: {
  meName: string;
  licenses: ClinicianLicense[];
  patientId: string;
  onPatientId: (v: string) => void;
  licenseId: string;
  onLicenseId: (v: string) => void;
  validUntil: string;
  onValidUntil: (v: string) => void;
  isEmergency: boolean;
  onIsEmergency: (v: boolean) => void;
  isRepeat: boolean;
  onIsRepeat: (v: boolean) => void;
  items: DraftItem[];
  onItemsChange: (items: DraftItem[]) => void;
  locked: boolean;
}) {
  const updateItem = (i: number, patch: Partial<DraftItem>) => {
    onItemsChange(items.map((it, j) => j === i ? { ...it, ...patch } : it));
  };
  const removeItem = (i: number) => {
    onItemsChange(items.filter((_, j) => j !== i));
  };
  const addItem = () => onItemsChange([...items, emptyItem()]);

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary"
                      sx={{ display: 'block', mb: 1.5 }}>
            Header
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="Patient ID" required
                value={patientId} onChange={(e) => onPatientId(e.target.value.replace(/[^0-9]/g, ''))}
                helperText="Numeric patient ID. Tour 4 wires a real picker."
                disabled={locked}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth select label="Issuing under license" required
                value={licenseId} onChange={(e) => onLicenseId(e.target.value)}
                disabled={locked || licenses.length === 0}
                helperText={licenses.length === 0
                  ? 'No prescribing license active.'
                  : `Issued by ${meName}`}
              >
                {licenses.filter((l) => l.can_prescribe && l.status === 'active').map((l) => (
                  <MenuItem key={l.id} value={String(l.id)}>
                    {l.jurisdiction_code} · {l.license_number}
                    {l.can_prescribe_controlled && ' · controlled OK'}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth label="Valid until" type="date"
                value={validUntil}
                onChange={(e) => onValidUntil(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={locked}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={<Switch
                  checked={isEmergency}
                  onChange={(_, v) => onIsEmergency(v)}
                  disabled={locked}
                />}
                label="Emergency prescription"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={<Switch
                  checked={isRepeat}
                  onChange={(_, v) => onIsRepeat(v)}
                  disabled={locked}
                />}
                label="Repeat / refillable"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="overline" color="text.secondary">
              Items ({items.length})
            </Typography>
            {!locked && (
              <Button size="small" startIcon={<AddIcon />} onClick={addItem}>
                Add medication
              </Button>
            )}
          </Stack>
          <Stack spacing={2}>
            {items.map((it, i) => (
              <ItemEditor
                key={i}
                item={it}
                disabled={locked}
                onChange={(patch) => updateItem(i, patch)}
                onRemove={items.length > 1 ? () => removeItem(i) : undefined}
              />
            ))}
          </Stack>
        </CardContent>
      </Card>

      {locked && (
        <Alert severity="info">
          Draft created. Continue to safety check, or use Back &amp; Edit
          to revise (creates a new draft).
        </Alert>
      )}
    </Stack>
  );
}

function ItemEditor({
  item, disabled, onChange, onRemove,
}: {
  item: DraftItem;
  disabled: boolean;
  onChange: (patch: Partial<DraftItem>) => void;
  onRemove?: () => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start"
               spacing={2} sx={{ mb: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            {item.drug_id == null ? (
              <Button variant="outlined" startIcon={<EditIcon />}
                      onClick={() => setPickerOpen(true)} disabled={disabled}>
                Choose medication…
              </Button>
            ) : (
              <Stack direction="row" spacing={2} alignItems="center">
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {item.drug_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary"
                              className="tabular">
                    ATC {item.drug_atc}
                  </Typography>
                </Box>
                {!disabled && (
                  <Button size="small" variant="text"
                          onClick={() => setPickerOpen(true)}>
                    Change
                  </Button>
                )}
              </Stack>
            )}
          </Box>
          {onRemove && (
            <IconButton onClick={onRemove} disabled={disabled} size="small"
                        aria-label="remove item">
              <DeleteIcon />
            </IconButton>
          )}
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth label="Dose / sig" required
              placeholder="1 tablet morning, 2 tablets evening"
              value={item.dose_text}
              onChange={(e) => onChange({ dose_text: e.target.value })}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth label="Quantity" required
              placeholder="60 tablets"
              value={item.quantity_text}
              onChange={(e) => onChange({ quantity_text: e.target.value })}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth label="Refills" type="number"
              value={item.refills}
              onChange={(e) => onChange({ refills: Math.max(0, parseInt(e.target.value || '0', 10)) })}
              inputProps={{ min: 0, max: 24 }}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth label="Indication ICD code"
              placeholder="J45.9"
              value={item.indication_icd_code}
              onChange={(e) => onChange({ indication_icd_code: e.target.value })}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={<Switch
                checked={item.is_substitution_allowed}
                onChange={(_, v) => onChange({ is_substitution_allowed: v })}
                disabled={disabled}
              />}
              label="Substitution allowed"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={<Switch
                checked={item.is_long_term}
                onChange={(_, v) => onChange({ is_long_term: v })}
                disabled={disabled}
              />}
              label="Long-term therapy"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth label="Notes to pharmacist"
              value={item.notes_to_pharmacist}
              onChange={(e) => onChange({ notes_to_pharmacist: e.target.value })}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth label="Notes to patient"
              multiline minRows={2}
              value={item.notes_to_patient}
              onChange={(e) => onChange({ notes_to_patient: e.target.value })}
              disabled={disabled}
            />
          </Grid>
        </Grid>
      </CardContent>

      <DrugPickerDialog
        open={pickerOpen} onClose={() => setPickerOpen(false)}
        onPick={(d) => {
          onChange({
            drug_id: d.id,
            drug_name: d.name,
            drug_atc: d.atc_code,
          });
          setPickerOpen(false);
        }}
      />
    </Card>
  );
}

function emptyItem(): DraftItem {
  return {
    drug_id: null, drug_name: '', drug_atc: '',
    dose_text: '', quantity_text: '', refills: 0,
    is_substitution_allowed: true, is_long_term: false,
    notes_to_pharmacist: '', notes_to_patient: '',
    indication_icd_code: '',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Drug picker — modal with type-ahead
// ─────────────────────────────────────────────────────────────────────────────

function DrugPickerDialog({
  open, onClose, onPick,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (drug: DrugCatalogEntry) => void;
}) {
  const [query, setQuery] = useState('');

  const q = useApiQuery({
    queryKey: ['drug-catalog', query],
    queryFn:  () => api.get<DrugCatalogEntry[]>('/api/medical/drug-catalog/', {
      params: { search: query, limit: 20 },
    }),
    enabled:  open,
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Choose medication</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus fullWidth
          placeholder="Search by name, generic, or ATC code"
          value={query} onChange={(e) => setQuery(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Stack spacing={1} sx={{ maxHeight: 360, overflow: 'auto' }}>
          {q.isPending && (
            <Typography variant="body2" color="text.secondary">Searching…</Typography>
          )}
          {q.isError && (
            <Alert severity="error" variant="outlined">{q.error?.message ?? 'Search failed'}</Alert>
          )}
          {(q.data ?? []).map((d) => (
            <Card
              key={d.id} variant="outlined"
              sx={{ cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
              onClick={() => onPick(d)}
            >
              <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body1" fontWeight={500} noWrap>
                      {d.name}
                      {d.strength && (
                        <Typography component="span" variant="body2" color="text.secondary">
                          {' '}· {d.strength} {d.form}
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary"
                                className="tabular">
                      {d.generic_name && `${d.generic_name} · `}ATC {d.atc_code}
                    </Typography>
                  </Box>
                  {d.is_controlled && (
                    <Chip size="small" color="warning" variant="outlined"
                          label={`controlled ${d.controlled_schedule || ''}`} />
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
          {q.data && q.data.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No matches.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 1 — Safety check
// ─────────────────────────────────────────────────────────────────────────────

function SafetyStep({
  snapshot, onRecheck, rechecking,
}: {
  snapshot: SafetySnapshot;
  onRecheck: () => void;
  rechecking: boolean;
}) {
  return (
    <Stack spacing={2}>
      <DDIPanel snapshot={snapshot} />
      <Stack direction="row" spacing={1}>
        <Button onClick={onRecheck} disabled={rechecking} variant="outlined">
          Re-run check
        </Button>
      </Stack>
    </Stack>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 2 — Sign
// ─────────────────────────────────────────────────────────────────────────────

function SignStep({ rx, blocked }: { rx: Prescription; blocked: boolean }) {
  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="overline" color="text.secondary">
              About to sign
            </Typography>
            <Typography variant="body1">
              Prescription <strong className="tabular">{rx.id.slice(0, 8)}</strong> for
              patient #{rx.patient}, {rx.items?.length ?? 0} medication(s),
              jurisdiction {rx.jurisdiction_code}.
            </Typography>
          </Box>

          {blocked ? (
            <Alert severity="error">
              Safety check is blocking this prescription. Go back, edit
              the items, and re-run the check.
            </Alert>
          ) : (
            <Alert severity="info">
              Signing is recorded with your signing key fingerprint and
              an audit row. If your jurisdiction has an e-prescription
              registry, the prescription is also submitted to it.
            </Alert>
          )}

          <Box>
            <Typography variant="caption" color="text.secondary">
              Items
            </Typography>
            <Stack spacing={1} sx={{ mt: 0.5 }}>
              {(rx.items ?? []).map((it) => (
                <Box key={it.id}>
                  <Typography variant="body2" fontWeight={600}>
                    {it.drug_name_snapshot}
                    {it.drug_strength_snapshot && (
                      <Typography component="span" variant="body2" color="text.secondary">
                        {' '}· {it.drug_strength_snapshot}
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {it.dose_text} · {it.quantity_text} · refills {it.refills}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
